import {
  PresentationContent,
  PresentationModification,
  ToolResult,
  ShapeModification,
  SlideCitationBinding,
} from "../../types";
import type { PptxLangfusePhase, PptxLangfuseRunContext } from "../../langfuseTrace";
import type {
  ModelThinkingEffort,
  OpenAIReasoningMode,
} from "../../../shared/modelPicker/catalog";
import type { AgentEvent } from "./agentStream";
import type { FinsterQueryDataSource } from "../../api/finster/constants";
import type { BrandId } from "./brandRegistry";
import type { DeckPlan } from "./deckPlannerService";
import type { SlideLibrarySlide } from "../../slideLibrary/be/types";
import type {
  FactSetPresentationRef,
  FactSetSelectedShapesResult,
  PreslinkCreationPresentationContext,
} from "../factset/intentBridgeService";
import type { GoldenArtifactRun } from "../goldenArtifactsService";
import type { ExtractWindowedPresentationContentOptions } from "../slideService";
import type { Citation } from "../../citations";
import type { SavedContext } from "../../../shared/api/finster/savedContext";
import {
  resolveFinsterBrandContext,
  type PublicBrandName,
  type PublicResearchToolName,
  type FinsterBrandContext,
} from "../../../shared/api/finster/queryTool";

// Brand context lives in the shared finster module so PowerPoint and Excel use
// one source of truth. Re-exported here to preserve existing PPT import sites.
export {
  FINSTER_RESEARCH_TOOL_NAME,
  FACTSET_RESEARCH_TOOL_NAME,
  isResearchToolName,
} from "../../../shared/api/finster/queryTool";
export { resolveFinsterBrandContext };
export type { PublicBrandName, PublicResearchToolName };
export type BrandContext = FinsterBrandContext;

export interface ToolExecutionPolicy {
  allowSlideImages?: boolean;
  /** Enables PresLink agent tooling for FactSet hosts known to support the required intents. */
  allowPreslink?: boolean;
  /** Enables PowerPoint ActiveGraph create/read/frame/delete/selected-edit tooling. */
  allowActiveGraph?: boolean;
  /** Enables creation of Preslink-backed ChartCreator images. */
  allowPreslinkChartCreation?: boolean;
  /**
   * Enables the DESTRUCTIVE PresLink processing branch (server-side workbook
   * recompute, source-file write-back, and slide replacement). This is a host
   * capability gate, not an intent classifier: the agent must explicitly choose
   * `operation:"refresh"`. Read-only `operation:"inspect"` calls are unaffected.
   */
  allowPreslinkProcessing?: boolean;
  /** Active company brand whose style prompt is appended, or null/undefined. */
  brandId?: BrandId | null;
  /** Optional prompt section for legacy/main-branch snippet layout guidance. Off unless explicitly enabled. */
  enableSnippetGuidance?: boolean;
  finsterDataSources?: readonly FinsterQueryDataSource[];
  /**
   * Marks `finsterDataSources` as endpoint-resolved Skill policy. In
   * particular, an explicit empty list means no ordinary research source.
   */
  finsterDataSourceScopeIsAuthoritative?: boolean;
  userFileIds?: readonly string[];
  publicBrandName?: PublicBrandName;
  publicResearchToolName?: PublicResearchToolName;
}

/**
 * FactSet type listing and identifier lookup serve both chart routes, so
 * either gate opens them; with neither open they stay closed.
 */
export function isFactSetChartHelpersEnabled(policy?: ToolExecutionPolicy): boolean {
  return policy?.allowActiveGraph === true || policy?.allowPreslinkChartCreation === true;
}

export interface ResearchToolScopeSnapshot {
  readonly tool: PublicResearchToolName;
  readonly query: string;
  readonly finsterDataSources?: readonly FinsterQueryDataSource[];
  readonly userFileIds?: readonly string[];
}

export interface CachedResearchToolExecution {
  readonly answer: string;
  readonly citations?: readonly Citation[];
  readonly sessionId?: string;
}

export interface CompletedResearchToolExecution
  extends ResearchToolScopeSnapshot, CachedResearchToolExecution {}

/**
 * Turn-scoped context for the unified agent's side-effecting tools
 * (apply_edits, get_slides_info). The orchestrator builds this once per turn
 * and threads it to executeTools; existing data tools ignore it. The FactSet
 * intent bridge is the only host.
 */
export interface AgentToolContext {
  /**
   * Rich prompt sent to the PresLink processing backend after the main agent has
   * explicitly selected the mutating tool operation. It may include task context
   * and bounded chat history, but never independently authorizes mutation.
   */
  preslinkProcessingPrompt?: string;
  /**
   * Contextual user prompt retained for backend processing and unaffected
   * non-agent call sites. PresLink intent and scope come from tool arguments.
   */
  userPrompt?: string;
  /**
   * Exact resolved Skill guidance forwarded to nested research calls.
   */
  selectedSkillsGuidance?: string;
  /** Successful planning research sink. It is absent for builds and polish. */
  onResearchResult?: (result: CompletedResearchToolExecution) => void | Promise<void>;
  /** Target presentation for apply/observe via the FactSet intent bridge. */
  presentationRef?: FactSetPresentationRef | null;
  /** Latest host-observed presentation after any in-turn slide replacement. */
  latestPresentationContent?: PresentationContent;
  /** Turn-scoped replay ledger for non-idempotent ActiveGraph creates. */
  activeGraphCreateResults?: Map<string, unknown>;
  /** Fail-closed after a create/edit may have mutated without confirmation. */
  activeGraphMutationIndeterminate?: boolean;
  /** Turn-scoped replay ledger for non-idempotent Preslink chart creates. */
  preslinkChartCreateResults?: Map<string, unknown>;
  /** Fail-closed after a Preslink chart insert may have mutated without confirmation. */
  preslinkChartMutationIndeterminate?: boolean;
  /**
   * Monotonic UTC-second allocator shared by every Preslink chart in the turn,
   * so two charts created back-to-back cannot be given the same filename.
   */
  preslinkChartTimestamps?: { next(): Date };
  /**
   * Deck location read once when the user submits the turn and shared by every
   * Preslink chart in it.
   */
  preslinkTurnPresentationContext?: Promise<PreslinkCreationPresentationContext>;
  /**
   * Shape selection read when the user submits the turn. A linked chart takes
   * over the shape the user had selected, so this must predate the agent's own
   * edits, which can move the selection.
   */
  preslinkTurnSelectedShapes?: Promise<FactSetSelectedShapesResult>;
  /** Extraction filters used to rebuild fresh model context after PresLink replacement. */
  preslinkExtractionFilters?: ExtractWindowedPresentationContentOptions["filters"];
  /** Prevents the agent from applying the same PresLink mutation twice in one turn. */
  preslinkProcessCompleted?: boolean;
  /**
   * True only when a completed PresLink refresh actually replaced slides. A
   * refresh that found no linked shapes completes without mutating the deck,
   * so duplicate calls must not claim slides were replaced.
   */
  preslinkPresentationApplied?: boolean;
  /** Turn-fatal reason from any failed mutating PresLink attempt. */
  preslinkMutationFailedReason?: string;
  /** Fail-closed guard when replacement completed but fresh managed objects could not be verified. */
  preslinkRefreshFailed?: boolean;
  /**
   * Post-refresh exclusion set: PresLink-managed shape ids keyed by their
   * (post-replacement) slide id. When present, apply_edits drops any
   * modify/delete targeting these shapes so native edits never re-touch the
   * objects a PresLink refresh already produced. Populated by the deterministic
   * refresh phase after re-extracting fresh ids.
   */
  preslinkExcludedShapeIdsBySlideId?: Map<string, Set<string>>;
  /**
   * Notifies the owning query flow that PresLink replaced slides and all ids must
   * be rebound before later tool hops or terminal output are handled.
   */
  reportPreslinkRefresh?: (info: {
    presentationContent: PresentationContent;
    refreshedSlideIds: string[];
    excludedShapeIdsBySlideId: Map<string, Set<string>>;
  }) => void;
  /** Slide bounds locked at turn start (points). */
  slideBounds?: { slideWidth: number; slideHeight: number } | null;
  /** Active golden-artifact run id for per-apply_edits-call capture. */
  goldenRunId?: string | null;
  /** Active golden-artifact run for tool-level capture in agentic deck builds. */
  goldenArtifactRun?: GoldenArtifactRun | null;
  /** Allocates deterministic per-tool subdirectories such as apply_00 or tool_00_get_slides_info. */
  nextGoldenToolSubdirectory?: (tool: string) => string;
  /**
   * Optional subdirectory for this run's `tool_trace.json`. The trace is normally
   * written top-level per turn; the post-build polish phase sets a per-slide
   * subdirectory (e.g. `polish_s01`) so parallel polish runs do not overwrite the
   * build's top-level trace or each other's.
   */
  goldenToolTraceSubdirectory?: string;
  /** Allocates deterministic subdirectories such as apply_00 for each mutating tool call. */
  nextGoldenArtifactSubdirectory?: () => string;
  /** Abort signal for the turn. */
  signal?: AbortSignal;
  /** Progress sink (deck-build UI). */
  onProgress?: (message: string) => void;
  /**
   * Structured per-apply progress sink for the deck-build UI: reports the slide
   * indices a successful apply_edits call touched and how many NEW slides it
   * inserted, so the orchestrator can advance per-slide progress.
   */
  reportApplyProgress?: (info: {
    affectedSlideIndices: number[];
    insertedCount: number;
    /** Real host slides created by this call (id-keyed, order-independent). */
    created?: Array<{ id: string; index: number }>;
    /** Real host ids of existing slides this call modified (refinement passes). */
    modifiedIds?: string[];
  }) => void;
  /**
   * Best-effort per-slide citation sink for task-grounded deck builds: reports
   * the apply pipeline's citation bindings (keyed by REAL host slide id) and the
   * declared cited values, so the orchestrator attaches SlideCitationsView
   * messages directly by id - no createdSlides diff or positional pairing.
   */
  reportCitationBindings?: (info: {
    /** Per-slide citation bindings keyed by real host slide id (from the manifest). */
    bindings: SlideCitationBinding[];
    citedValuesByUuid: Record<string, string>;
  }) => void;
  /**
   * Slide-library PPTX sources keyed by librarySlideId, used by the
   * insert_library_slide tool during deck builds so the executor can resolve a
   * library slide id to its PPTX without a network round-trip and without the
   * agent ever handling URLs. Populated by the deck-build orchestrator.
   */
  librarySlideSourcesById?: Map<string, { title?: string; pptxSources: string[] }>;
  /**
   * Company id used to list company slide-library collections. Empty when no
   * brand/company is resolved; library browse can still return personal
   * collections for a named lookup.
   */
  companyId?: string;
  /** Slide metadata remembered during this turn's library browse/preview calls. */
  librarySlideCache?: Map<string, SlideLibrarySlide>;
  /**
   * True after search_slide_library returned READY catalog slides this turn.
   * Used to nudge present_deck_plan when the agent omitted librarySlideId.
   */
  libraryCatalogHadSlides?: boolean;
  /** Unconfirmed Review plan for this chat turn, if the Review dialog is open. */
  pendingDeckPlan?: DeckPlan;
  /**
   * Latest unconfirmed Review plan, including titles/bullets the user already
   * edited in the dialog. `pendingDeckPlan` is only the last agent snapshot.
   */
  getPendingDeckPlan?: () => DeckPlan | undefined;
  /**
   * True while Review is opening or open. False after cancel, confirm, or error.
   * Preferred over `blockNewSlideCreation` for same-turn checks because cancel
   * does not mutate the turn-scoped snapshot.
   */
  isReviewSessionOpen?: () => boolean;
  /** When true, insert/duplicate/library-insert must wait for Create slides. */
  blockNewSlideCreation?: boolean;
  onPresentDeckPlan?: (plan: DeckPlan) => void | Promise<void>;
  onUpdateDeckPlan?: (plan: DeckPlan) => boolean | void;
  /**
   * Absolute logo URLs keyed by normalized company name from get_company_logo
   * in this turn. apply_edits expands shape.logoCompanyName against this map
   * for legacy single-result payloads.
   */
  logoUrlsByCompanyKey?: Map<string, string>;
  /**
   * Candidate proxy URLs keyed by opaque refs returned from get_company_logo.
   * apply_edits expands the model-selected shape.logoCandidateRef through this
   * map so the selected URL never enters model context.
   */
  logoUrlsByCandidateRef?: Map<string, string>;
  /** Diagnostic logger. */
  log?: (message: string) => void;
}

/**
 * Brand reference-deck examples for the deck-build seed turn: one entry per
 * reference slide (serialized shape JSON + an optional rendered image). The
 * OpenAI-native path interleaves each slide's JSON with its image so the model
 * gets a tight image-to-spec binding.
 */
export interface ReferenceDeckExamples {
  /** Preamble shown once before the per-slide examples. */
  intro: string;
  /**
   * One entry per reference slide, in deck order. `json` is omitted in curated
   * mode, where an authored `caption` and the brand style prompt carry the
   * geometry instead of a serialized shape dump.
   */
  slides: Array<{ label: string; json?: string; caption?: string; image?: string }>;
  /** Trailer shown once after the per-slide examples. */
  outro: string;
}

export interface AiRequestOptions {
  signal?: AbortSignal;
  /** Snapshotted once per run; defaults to the production `low` setting. */
  reasoningEffort?: ModelThinkingEffort;
  /** OpenAI-only reasoning mode. Omitted for models without mode support. */
  reasoningMode?: OpenAIReasoningMode;
  agentToolContext?: AgentToolContext;
  /**
   * Ordered user-role messages inserted before the main request. Native
   * continuations replay them; each fresh legacy request restores them.
   */
  preludeUserMessages?: readonly string[];
  /**
   * The user's saved setup for this run. Set only by `queryOpenAI`, which also puts its block in
   * the preludes.
   */
  savedContext?: SavedContext | null;
  /** Brand reference examples to attach to the initial deck-build turn. */
  referenceExamples?: ReferenceDeckExamples;
  /** Per-run Langfuse trace context, propagated as request metadata to /api/ai/*. */
  langfuseContext?: PptxLangfuseRunContext;
  /**
   * Entry phase for this run's seed provider call ("chat" or "deck_build").
   * Continuation hops in the agent loop are tagged "multi_hop_executor" so they
   * do not clobber the trace root input. Defaults to "chat".
   */
  langfusePhase?: PptxLangfusePhase;
  /**
   * Opt-in deck-build UX metadata: when true, stream only the safe summary
   * fields from large apply_edits arguments so the progress UI can show
   * per-slide drafting status before the full JSON payload completes.
   */
  streamApplyEditSlideSummaries?: boolean;
}

/**
 * Resolve the brand context from a PowerPoint tool-execution policy. Thin
 * wrapper over the shared {@link resolveFinsterBrandContext} so brand strings
 * live in one place (`shared/api/finster/queryTool.ts`).
 */
export function getBrandContext(policy?: ToolExecutionPolicy): BrandContext {
  return resolveFinsterBrandContext(policy?.publicBrandName, policy?.publicResearchToolName);
}

// Unified streaming callback surface for the agent. `onShapeReady` is optional
// (vestigial on the FactSet bridge, which applies edits as a batch per
// apply_edits call rather than per streamed shape) and `onProgress` carries
// deck-build progress to the UI. Deck generation uses the `DeckStreamCallbacks`
// specialization below where per-shape streaming is still wired.
export interface StreamCallbacks {
  onThinking: (text: string) => void;
  onToolCall: (tool: string, args: string) => void;
  onToolResult?: (results: ToolResult[]) => void;
  onShapeReady?: (shape: ShapeModification, slideIndex: number) => Promise<void>;
  onProgress?: (message: string) => void;
  onComplete: (modifications: PresentationModification) => void;
  onError: (error: string) => void;
  /**
   * Additive, fine-grained streaming sink for the live agent timeline (hop
   * thinking deltas, output deltas, tool calls/results, nested sub-agent
   * events). Existing callbacks above are unchanged; consumers that don't need
   * the timeline can ignore this.
   */
  onAgentEvent?: (event: AgentEvent) => void;
}

export interface DeckStreamCallbacks extends StreamCallbacks {
  onShapeReady: (shape: ShapeModification, slideIndex: number) => Promise<void>;
}

export interface SlideQAResult {
  slideIndex: number;
  issues: SlideIssue[];
  overallScore: number;
  summary: string;
  keyFacts?: KeyFact[];
}

export interface SlideIssue {
  type: "typo" | "grammar" | "factual" | "design" | "clarity" | "consistency";
  severity: "low" | "medium" | "high";
  description: string;
  suggestion?: string;
  location?: string;
}

export interface KeyFact {
  category: "number" | "percentage" | "date" | "name" | "metric";
  label: string;
  value: string;
  slideIndex: number;
}

export interface SlideQACallbacks {
  onProgress: (slideIndex: number, totalSlides: number) => void;
  onSlideResult: (result: SlideQAResult) => void;
  onComplete: (results: SlideQAResult[]) => void;
  onError: (error: string) => void;
  onThinking?: (slideIndex: number, text: string) => void;
}

export interface VisionReviewResult {
  needsRefinement: boolean;
  feedback: string;
  suggestedChanges?: PresentationModification;
}

export interface VisionReviewCallbacks {
  onProgress?: (message: string) => void;
  onThinking?: (text: string) => void;
  onComplete: (result: VisionReviewResult) => void;
  onError: (error: string) => void;
}
